import React from 'react';
import { InputNumber as AntInputNumber, InputNumberProps as AntInputNumberProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface InputNumberProps extends Omit<AntInputNumberProps, 'className'> {
  className?: string;
  status?: 'error' | 'warning';
}

const useStyles = createUseStyles((theme: ThemeType) => {
  return {
    inputNumber: {
      width: '100%',
      height: '32px',
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderColor: theme.color.semantic.border.primary,
      borderWidth: theme.border.width.sm,
      borderRadius: theme.radius.sm,
      borderStyle: 'solid',
      transition: 'all 0.2s ease',

      '&.ant-input-number': {
        height: '32px',
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.border.primary,
        borderWidth: theme.border.width.sm,
        borderRadius: theme.radius.sm,
      },

      '& .ant-input-number-input': {
        color: theme.color.semantic.text.primary,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.regular,
        lineHeight: theme.typography.lineHeight.normal,
        padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
        backgroundColor: 'transparent',
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
      },

      '& .ant-input-number-handler-wrap': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderLeft: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
        borderRadius: `${theme.radius.none} ${theme.radius.sm} ${theme.radius.sm} ${theme.radius.none}`,
        opacity: theme.opacity[0],
      },

      '& .ant-input-number-handler': {
        borderColor: theme.color.semantic.border.primary,
        color: theme.color.semantic.text.soft,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: theme.color.semantic.background.neutral.secondary,
          color: theme.color.semantic.brand.default.primary,
          height: '50% !important',
        },
        '&:active': {
          backgroundColor: theme.color.semantic.background.neutral.secondary,
        },
      },

      '& .ant-input-number-handler-up': {
        borderTopRightRadius: theme.radius.sm,
      },

      '& .ant-input-number-handler-down': {
        borderBottomRightRadius: theme.radius.sm,
      },

      '& .ant-input-number-handler-up-inner, & .ant-input-number-handler-down-inner': {
        color: 'inherit',
        fontSize: theme.typography.fontSize.xs,
      },

      '&:hover': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.brand.default.soft,
        '& .ant-input-number-handler-wrap': {
          opacity: theme.opacity[100],
          borderLeftColor: theme.color.semantic.brand.default.soft,
        },
      },

      '&:focus, &.ant-input-number-focused': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.brand.default.moderate,
        '& .ant-input-number-handler-wrap': {
          opacity: theme.opacity[100],
          borderLeftColor: theme.color.semantic.brand.default.moderate,
        },
      },

      '&.ant-input-number-disabled': {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
        opacity: `${theme.opacity[70]} !important`,
        borderColor: theme.color.semantic.border.primary,
        cursor: 'not-allowed',
        '& .ant-input-number-input': {
          color: theme.color.semantic.text.soft,
          cursor: 'not-allowed',
          '&::placeholder': {
            color: theme.color.semantic.text.soft,
          },
        },
        '& .ant-input-number-handler-wrap': {
          backgroundColor: theme.color.semantic.background.neutral.subtle,
          opacity: `${theme.opacity[70]} !important`,
          borderLeftColor: theme.color.semantic.border.primary,
        },
        '& .ant-input-number-handler': {
          cursor: 'not-allowed',
          color: theme.color.semantic.text.soft,
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.color.semantic.text.soft,
          },
        },
      },

      '&.input-number-status-error': {
        borderColor: theme.color.semantic.brand.danger.light,
        '&:hover': {
          borderColor: theme.color.semantic.brand.danger.light,
        },
        '&:focus, &.ant-input-number-focused': {
          borderColor: theme.color.semantic.brand.danger.light,
        },
        '& .ant-input-number-handler-wrap': {
          borderLeftColor: theme.color.semantic.brand.danger.light,
        },
      },

      '&.input-number-status-warning': {
        borderColor: theme.color.semantic.accent.orange.strong,
        '&:hover': {
          borderColor: theme.color.semantic.accent.orange.strong,
        },
        '&:focus, &.ant-input-number-focused': {
          borderColor: theme.color.semantic.accent.orange.strong,
        },
        '& .ant-input-number-handler-wrap': {
          borderLeftColor: theme.color.semantic.accent.orange.strong,
        },
      },

      '&.ant-input-number-sm': {
        height: '24px',
        '& .ant-input-number-input': {
          padding: `${theme.spacing[0]} ${theme.spacing[3]}`,
          fontSize: theme.typography.fontSize.sm,
        },
        '& .ant-input-number-handler-up-inner, & .ant-input-number-handler-down-inner': {
          fontSize: theme.typography.fontSize.xxs,
        },
      },

      '&.ant-input-number-lg': {
        height: '40px',
        '& .ant-input-number-input': {
          padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
          fontSize: theme.typography.fontSize.md,
        },
        '& .ant-input-number-handler-up-inner, & .ant-input-number-handler-down-inner': {
          fontSize: theme.typography.fontSize.sm,
        },
      },

      '& .ant-input-number-prefix': {
        color: theme.color.semantic.text.soft,
        marginRight: theme.spacing[2],
      },

      '&.ant-input-number-disabled .ant-input-number-prefix': {
        color: theme.color.semantic.text.soft,
      },

      '&.ant-input-number-borderless': {
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        '&:hover': {
          borderColor: 'transparent',
        },
        '&:focus, &.ant-input-number-focused': {
          borderColor: 'transparent',
          boxShadow: 'none',
        },
        '& .ant-input-number-handler-wrap': {
          borderLeftColor: 'transparent',
        },
      },
    },
  };
});

const InputNumber: React.FC<InputNumberProps> = ({
  size = 'middle',
  className = '',
  status,
  ...rest
}) => {
  const classes = useStyles();

  const statusClass = status ? `input-number-status-${status}` : '';
  const combinedClassName = `${classes.inputNumber} ${statusClass} ${className}`.trim();

  return (
    <AntInputNumber
      size={size}
      {...rest}
      className={combinedClassName}
    />
  );
};

export default InputNumber;
